import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Camera } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { CroppedImage } from '@/components/ui/cropped-image';
import { Skeleton } from '@/components/ui/skeleton';
import { Event } from '@/types';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { coverUrl } from '@/utils/coverImage';

interface EventsListProps {
  onEventSelect: (event: Event) => void;
}

// Explicit column list — avoid select('*') over-fetch.
// Only columns actually rendered on the homepage cards.
const EVENTS_COLUMNS = [
  'id',
  'swiss_equestrian_id',
  'name',
  'location',
  'start_date',
  'end_date',
  'status',
  'created_at',
  'cover_image_url',
  'cover_crop_top',
  'total_photos_cached',
].join(', ');

// Homepage only needs to know if a race has uploads + its date to categorise events.
const RACES_COLUMNS = ['participant_list_uploaded', 'race_date'].join(', ');

async function fetchHomepageEvents(): Promise<Event[]> {
  // Cap to the last 12 months on the initial load — older events are reachable
  // via search/direct link. Prevents the homepage payload from growing forever.
  const cutoff = new Date();
  cutoff.setMonth(cutoff.getMonth() - 12);

  const { data, error } = await supabase
    .from('events')
    .select(`${EVENTS_COLUMNS}, races(${RACES_COLUMNS})`)
    .gte('start_date', cutoff.toISOString().slice(0, 10))
    .order('start_date', { ascending: false });
  if (error) throw error;

  return (data || []).map((event: any) => ({
    id: event.id,
    swiss_equestrian_id: event.swiss_equestrian_id,
    name: event.name,
    location: event.location,
    start_date: event.start_date,
    end_date: event.end_date,
    status: (event.status as 'planned' | 'upcoming' | 'active' | 'completed') || 'planned',
    created_at: event.created_at || new Date().toISOString(),
    updated_at: event.created_at || new Date().toISOString(),
    cover_image_url: event.cover_image_url || undefined,
    cover_crop_top: event.cover_crop_top || undefined,
    total_photos: event.total_photos_cached || 0,
    races:
      event.races?.map((race: any) => ({
        // Only the two fields homepage categorisation reads.
        participant_list_uploaded: race.participant_list_uploaded,
        race_date: race.race_date,
      })) || [],
  })) as any as Event[];
}


export const EventsList = ({ onEventSelect: _onEventSelect }: EventsListProps) => {
  const { t } = useTranslation();

  // Single cached query — react-query dedupes across components and avoids
  // refetching on navigation/focus.
  const { data: events = [], isLoading: loading } = useQuery({
    queryKey: ['homepage-events'],
    queryFn: fetchHomepageEvents,
    staleTime: 5 * 60_000,
  });

  const categorizeEvents = () => {
    const now = new Date();
    const twoWeeksAgo = new Date();
    twoWeeksAgo.setDate(now.getDate() - 14);

    const upcoming: Event[] = [];
    const recent: Event[] = [];
    const archive: Event[] = [];

    events.forEach((event) => {
      const eventEndDate = new Date(event.end_date);
      const hasUploadedRaces = event.races?.some((race) => race.participant_list_uploaded);

      if (hasUploadedRaces) {
        const eventCreated = new Date(event.created_at);
        if (eventCreated > twoWeeksAgo) recent.push(event);
        else archive.push(event);
      } else {
        if (eventEndDate >= now) upcoming.push(event);
        else archive.push(event);
      }
    });

    return { upcoming, recent, archive };
  };

  const formatEventDates = (startDate: string, endDate: string) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (startDate === endDate) return format(start, 'MMM d, yyyy');
    return `${format(start, 'MMM d')} - ${format(end, 'd, yyyy')}`;
  };

  const renderEventCard = (event: Event, fillHeight: boolean = false) => {
    const photoCount = (event as any).total_photos as number | undefined;
    return (
      <Link to={`/${event.swiss_equestrian_id}`} key={event.id} className={`block group ${fillHeight ? 'h-full' : ''}`}>
        <div className={`bg-surface-container-lowest rounded-md shadow-ambient overflow-hidden hover:shadow-float transition-shadow duration-300 ${fillHeight ? 'h-full flex flex-col' : ''}`}>
          <div className={`relative overflow-hidden ${fillHeight ? 'flex-1 min-h-[12rem]' : 'h-44 md:h-48'}`}>
            <CroppedImage
              src={coverUrl(event.cover_image_url, fillHeight ? 'hero' : 'card') || 'https://images.unsplash.com/photo-1553978297-833d24c4736f?w=600&h=300&fit=crop'}
              alt={event.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              cropTop={event.cover_crop_top}
              fallbackSrc='https://images.unsplash.com/photo-1553978297-833d24c4736f?w=600&h=300&fit=crop'
            />
            {photoCount && photoCount > 0 ? (
              <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-on-surface/60 text-white rounded-full px-2.5 py-1 text-xs font-medium backdrop-blur-sm">
                <Camera className="w-3 h-3" />
                {photoCount}
              </div>
            ) : null}
          </div>

          <div className="p-4 md:p-5">
            <h3 className="font-serif font-semibold text-on-surface group-hover:text-primary transition-colors text-base md:text-lg">
              {event.name}
            </h3>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {event.location}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {formatEventDates(event.start_date, event.end_date)}
              </span>
            </div>
          </div>
        </div>
      </Link>
    );
  };

  const renderMasonryGrid = (eventsList: Event[]) => {
    const elements: React.ReactNode[] = [];
    let i = 0;

    while (i < eventsList.length) {
      const remaining = eventsList.length - i;
      const rowIndex = Math.floor(i / 5);

      if (remaining === 1) {
        elements.push(
          <div key={`row-${rowIndex}-single`} className="grid grid-cols-1 md:grid-cols-5 gap-5 lg:gap-6">
            <div className="md:col-span-3">{renderEventCard(eventsList[i], true)}</div>
          </div>
        );
        i += 1;
      } else if (remaining >= 2 && i % 5 < 2) {
        elements.push(
          <div key={`row-${rowIndex}-a`} className="grid grid-cols-1 md:grid-cols-5 items-stretch gap-5 lg:gap-6">
            <div className="md:col-span-3">{renderEventCard(eventsList[i], true)}</div>
            <div className="md:col-span-2">{renderEventCard(eventsList[i + 1], true)}</div>
          </div>
        );
        i += 2;
      } else if (remaining >= 3) {
        elements.push(
          <div key={`row-${rowIndex}-b`} className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6">
            {renderEventCard(eventsList[i])}
            {renderEventCard(eventsList[i + 1])}
            {renderEventCard(eventsList[i + 2])}
          </div>
        );
        i += 3;
      } else {
        elements.push(
          <div key={`row-${rowIndex}-rem`} className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6">
            {eventsList.slice(i).map((e) => renderEventCard(e))}
          </div>
        );
        i = eventsList.length;
      }
    }

    return <div className="flex flex-col gap-5 lg:gap-6">{elements}</div>;
  };

  const renderSection = (title: string, subtitle: string, sectionEvents: Event[]) => {
    if (sectionEvents.length === 0) return null;
    return (
      <div className="mb-16 md:mb-20">
        <div className="flex items-end justify-between mb-6 md:mb-8">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-on-surface font-serif">{title}</h2>
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-sans mt-1">{subtitle}</p>
          </div>
        </div>
        {renderMasonryGrid(sectionEvents)}
      </div>
    );
  };

  const renderSkeletons = () => (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <div className="mb-8">
        <Skeleton className="h-8 w-56 mb-2" />
        <Skeleton className="h-3 w-72" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-5 lg:gap-6 mb-6">
        <div className="md:col-span-3">
          <Skeleton className="h-72 w-full rounded-md mb-4" />
          <Skeleton className="h-6 w-3/4 mb-2" />
          <Skeleton className="h-3 w-1/2" />
        </div>
        <div className="md:col-span-2">
          <Skeleton className="h-48 w-full rounded-md mb-4" />
          <Skeleton className="h-5 w-3/4 mb-2" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6">
        {[...Array(3)].map((_, i) => (
          <div key={i}>
            <Skeleton className="h-48 w-full rounded-md mb-4" />
            <Skeleton className="h-5 w-3/4 mb-2" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        ))}
      </div>
    </div>
  );

  if (loading) return renderSkeletons();

  const { upcoming, recent, archive } = categorizeEvents();

  return (
    <div id="events-section" className="max-w-7xl mx-auto px-4 py-12 md:py-16">
      {renderSection(t('events.upcoming'), t('events.subtitle'), upcoming)}
      {renderSection(t('events.recent'), t('events.subtitle'), recent)}
      {renderSection(t('events.archive'), t('events.subtitle'), archive)}

      {events.length === 0 && !loading && (
        <div className="text-center py-20">
          <Camera className="w-16 h-16 mx-auto text-muted-foreground mb-6" />
          <h3 className="text-2xl font-semibold mb-3 font-serif text-on-surface">{t('events.noEvents')}</h3>
          <p className="text-base text-muted-foreground max-w-md mx-auto">
            {t('events.noEventsDescription')}
          </p>
        </div>
      )}
    </div>
  );
};
